import React, { useEffect, useState } from 'react';
import { PAYMENT_LIVE, RESEND_LIVE, addJobNote, approvePlan, isOnHold, isVisit, loadJob, resendInvoice, sendBackPlan, stepDate, useJob } from '../../services/jobs';
import { CURRENT_USER } from '../../services/currentUser';
import { APP_CALLS_LIVE, callCustomer } from '../../services/appCall';
import { avatarColours, initials } from '../Calls/avatar';
import { stageChip } from './JobProgress';
import PlanEditor from './PlanEditor';
import StepButtons from './StepButtons';
import RecordPayment from './RecordPayment';
import JobActions from './JobActions';
import '../Customers/CustomerAccount.css';
import '../Routes/StatusUpdateSheet.css';
import './Jobs.css';

/**
 * One job — live from Zoho (services/jobs.js): the customer, the plan and its steps, schedule, payment, notes.
 * Plan: set up → Submit → admin Approve (job goes In Progress, first step current) or Send back with a reason;
 * once approved only an admin edits it; steps are ticked / booked under it (StepButtons).
 * Job: on hold / resume, cancel (admin). Payment: resend, pay link, record e-transfer/cash. Notes: add one.
 */

const money = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (iso) =>
  iso ? new Date(String(iso).length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

const Row = ({ label, children }) =>
  children ? (
    <div>
      <span>{label}</span>
      <b>{children}</b>
    </div>
  ) : null;
const visitsOf = (steps) => steps.filter(isVisit);

const JobDetail = ({ id, onBack, onOpenCustomer }) => {
  const { data, loading, error } = useJob(id);
  const [editing, setEditing] = useState(false);
  const [sendingBack, setSendingBack] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    loadJob(id);
  }, [id]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const act = async (fn) => {
    setBusy(true);
    try {
      setToast(await fn());
      setSendingBack(false);
      setReason('');
    } catch (err) {
      setToast(`⚠ ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const j = data && data.job;
  const [bg, fg] = avatarColours(j ? j.name : '');
  const chip = j ? stageChip(j) : null;
  const steps = (j && j.plan && j.plan.steps) || [];
  const isAdmin = CURRENT_USER.admin === true;
  const closed = j && (j.stage === 'Completed' || j.stage === 'Cancelled');
  const approved = j && j.planStatus === 'Approved';
  // not set up yet / sent back → straight into the editor; pending → admin decides; approved → admin may edit
  const showEditor = j && !closed && (editing || ['Not Set Up', 'Change Requested'].includes(j.planStatus));

  return (
    <div className="ca">
      <div className="ca-bar">
        <button type="button" className="ca-back" onClick={onBack} aria-label="Back">‹</button>
        <div className="ca-bar-title">Job</div>
        <button type="button" className="ca-refresh" onClick={() => loadJob(id)} disabled={loading} aria-label="Reload from Zoho">
          {loading ? '…' : '↻'}
        </button>
      </div>

      <div className="ca-scroll">
        {!j && loading && <p className="ca-empty">Loading from Zoho…</p>}
        {!j && error && (
          <p className="ca-empty error">
            {error} <button type="button" onClick={() => loadJob(id)}>Try again</button>
          </p>
        )}

        {j && (
          <>
            <div className="ca-profile">
              <div className="ca-avatar" style={{ background: bg, color: fg }}>{initials(j.name)}</div>
              <div className="ca-who">
                <div className="ca-name">{j.name}</div>
                <span className={`job-pill ${chip.tone}`}>{chip.text}</span>
              </div>
            </div>
            <div className="ca-contact">
              {j.phone && <div>📱 {j.phone}</div>}
              {j.email && <div>✉️ {j.email}</div>}
              {j.address && <div>📍 {j.address}{j.postalCode ? ` · ${j.postalCode}` : ''}</div>}
            </div>
            <div className="ca-actions">
              {j.phone &&
                (APP_CALLS_LIVE ? (
                  <button type="button" onClick={() => callCustomer(j.phone, j.name)}>📞<span>Call</span></button>
                ) : (
                  <a href={`tel:${j.phone}`}>📞<span>Call</span></a>
                ))}
              {j.address && (
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(j.address)}`} target="_blank" rel="noopener noreferrer">🗺<span>Map</span></a>
              )}
              {j.inspectionId && onOpenCustomer && (
                <button type="button" onClick={() => onOpenCustomer(j.inspectionId)}>👤<span>360° View</span></button>
              )}
            </div>

            <section className="ca-section">
              <div className="ca-section-head"><h3>Job</h3></div>
              <div className="ca-rows">
                <Row label="Type">{j.type || 'Not set'}</Row>
                <Row label="Package">{j.package}</Row>
                <Row label="Agent">{j.agent || 'Not set'}</Row>
                <Row label="Rebate job (Enbridge)">{j.rebate ? 'Yes' : 'No'}</Row>
                <Row label="Deposit required">{j.depositRequired ? 'Yes' : 'No'}</Row>
                <Row label="Plan">{j.planStatus}</Row>
                <Row label="Current step">{j.step}</Row>
              </div>
              {j.changeRequest && (
                <div className="ca-booking-notes">
                  <div className="ca-label">Change requested</div>
                  <p>{j.changeRequest}</p>
                </div>
              )}
              {isOnHold(j) && (
                <div className="ca-booking-notes">
                  <div className="ca-label">On hold</div>
                  <p>{j.step.replace(/^⏸ On hold — /, '')}</p>
                </div>
              )}
              {!closed && <JobActions key={j.step} job={j} isAdmin={isAdmin} onToast={setToast} />}
            </section>

            <section className="ca-section">
              <div className="ca-section-head">
                <h3>Plan</h3>
                {approved && isAdmin && !closed && !showEditor && (
                  <button type="button" className="plan-link" onClick={() => setEditing(true)}>Edit plan</button>
                )}
              </div>
              {showEditor ? (
                <PlanEditor
                  key={`${j.id}-${j.planStatus}`}
                  job={j}
                  approved={approved}
                  onDone={(msg) => { setEditing(false); setToast(msg); }}
                  onCancel={() => setEditing(false)}
                />
              ) : steps.length > 0 ? (
                <ol className="job-steps">
                  {steps.map((s, i) => (
                    <li key={s.key || i} className={`job-step ${s.status || 'pending'}`}>
                      <span className="job-step-dot" aria-hidden="true">{s.status === 'done' ? '✓' : s.status === 'current' ? '●' : '○'}</span>
                      <span>{s.label}</span>
                      {approved && <em className={stepDate(s, j).tone}>{stepDate(s, j).text}</em>}
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="ca-muted">{j.planSummary || 'No plan yet.'}</p>
              )}
              {!showEditor && approved && j.stage !== 'Cancelled' && steps.length > 0 && (
                <StepButtons
                  key={(steps.find((s) => s.status === 'current') || {}).key || 'all-done'}
                  job={j}
                  steps={steps}
                  isAdmin={isAdmin}
                  onToast={setToast}
                />
              )}
              {!showEditor && j.planStatus === 'Pending Approval' && (
                isAdmin ? (
                  <div className="plan-approve">
                    {sendingBack ? (
                      <>
                        <textarea className="status-textarea" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What needs changing?" />
                        <div className="plan-actions">
                          <button type="button" className="status-cancel" onClick={() => setSendingBack(false)} disabled={busy}>Cancel</button>
                          <button type="button" className="status-save moved" onClick={() => act(() => sendBackPlan(j.id, reason))} disabled={busy || !reason.trim()}>
                            {busy ? 'Sending…' : 'Send back'}
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="plan-actions">
                        <button type="button" className="status-cancel" onClick={() => setEditing(true)} disabled={busy}>Edit</button>
                        <button type="button" className="status-cancel" onClick={() => setSendingBack(true)} disabled={busy}>Send back</button>
                        <button type="button" className="status-save" onClick={() => act(() => approvePlan(j.id))} disabled={busy}>{busy ? 'Approving…' : '✓ Approve plan'}</button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="ca-muted">Waiting for an admin to approve.</p>
                )
              )}
            </section>

            <section className="ca-section">
              <div className="ca-section-head"><h3>Schedule</h3></div>
              <div className="ca-rows">
                {visitsOf(steps).map((s) => (
                  <Row key={s.key} label={s.label}>{stepDate(s, j).text}</Row>
                ))}
                <Row label="Visits">{visitsOf(steps).length || j.visits}</Row>
                <Row label="Completed">{date(j.completedDate)}</Row>
              </div>
              {!visitsOf(steps).some((s) => s.when) && !j.completedDate && <p className="ca-muted">Nothing booked yet.</p>}
            </section>

            <section className="ca-section">
              <div className="ca-section-head"><h3>Payment</h3></div>
              <div className="ca-rows">
                <Row label="Invoice">{j.invoiceNumber || 'Not sent yet — send it from Calls → Booked'}</Row>
                <Row label="Invoice total">{j.total != null ? money(j.total) : null}</Row>
                <Row label="Paid">{j.paid != null ? money(j.paid) : null}</Row>
                <Row label="Balance">{j.balance != null ? money(j.balance) : null}</Row>
                <Row label="Deposit received">{date(j.depositPaid)}</Row>
              </div>
              {j.invoiceLink && (
                <div className="plan-actions">
                  {RESEND_LIVE && (
                    <button type="button" className="status-cancel" disabled={busy} onClick={() => act(() => resendInvoice(j.id))}>
                      {busy ? 'Sending…' : '🧾 Resend invoice'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="status-cancel"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(j.invoiceLink);
                        setToast('🔗 Pay link copied');
                      } catch {
                        window.prompt('Copy the pay link:', j.invoiceLink);
                      }
                    }}
                  >
                    🔗 Copy pay link
                  </button>
                </div>
              )}
              {j.invoiceLink && (
                <a className="plan-link pay-page" href={j.invoiceLink} target="_blank" rel="noopener noreferrer">Open the invoice / pay page ↗</a>
              )}
              {PAYMENT_LIVE && j.invoiceNumber && Number(j.balance) > 0 && j.stage !== 'Cancelled' && (
                <RecordPayment job={j} onToast={setToast} />
              )}
            </section>

            <section className="ca-section">
              <div className="ca-section-head">
                <h3>Notes</h3>
                {!noteOpen && <button type="button" className="plan-link" onClick={() => setNoteOpen(true)}>+ Add note</button>}
              </div>
              {noteOpen && (
                <div className="step-form">
                  <textarea className="status-textarea" rows={3} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Saved on the job in Zoho with your name" />
                  <div className="plan-actions">
                    <button type="button" className="status-cancel" onClick={() => setNoteOpen(false)} disabled={busy}>Cancel</button>
                    <button
                      type="button"
                      className="status-save"
                      disabled={busy || !noteText.trim()}
                      onClick={() => act(async () => {
                        const msg = await addJobNote(j.id, noteText.trim());
                        setNoteOpen(false);
                        setNoteText('');
                        return msg;
                      })}
                    >
                      {busy ? 'Saving…' : 'Save note'}
                    </button>
                  </div>
                </div>
              )}
              {!data.notes.length && <p className="ca-muted">No notes yet.</p>}
              {data.notes.map((n) => (
                <div className="ca-note" key={n.id}>
                  <div className="ca-note-head">
                    <b>{n.title || n.author}</b>
                    <span>{date(n.at)}</span>
                  </div>
                  <p>{n.text}</p>
                </div>
              ))}
            </section>
          </>
        )}
      </div>
      {toast && <div className="plan-toast" role="status">{toast}</div>}
    </div>
  );
};

export default JobDetail;
